import { Arrow, CloseIcon, EditIcon, EyeIcon, PlusIcon, WalletIcon, WizardIcon } from '~/svgs';
import { NextLink, Translate } from '~/i18n';
import React, { useEffect, useState } from 'react';

import { Button } from '~/components/common/button';
import { DateTime } from 'luxon';
import Dropdown from '~/components/common/dropdown';
import Image from 'next/image';
import Pagination from '~/components/common/pagination';
import { ProductProps } from '~/interfaces/product';
import SearchFilter from './search-filter';
import { Section } from '../common/section';
import StatusFilter from './status-filter';
import { SubmitLoader } from '~/components/common/loaders/submit';
import cn from 'classnames';
import { useAuth } from '~/auth';
import useProductList from '~/components/products-list/useProductList.api';
import { useRouter } from 'next/router';
import { useTranslate } from '~/i18n';
import useUserProfile from '~/apis/useUserProfile';
import BnplInfoCard from '~/components/bnpl/bnpl-info-card';
import useBnplStatus from '~/apis/bnpl/useBnplStatus';
import CreateProductModal from '../create-product/create-product-modal';
import UnpublishProductModal from '../create-product/unpublish-product-modal';

// import DeleteProductModal from '../create-product/delete-product-modal';
// import RefundTicketsModal from '~/components/product-details/refund-tickets';
// import { useAuth } from '~/auth';

// import { Popover, ArrowContainer } from 'react-tiny-popover';

const getStatus = (status: any) => {
   switch (status) {
      case 'Under Review':
         return {
            text: <Translate id="productsList:under review" />,
            color: 'bg-light-blue',
         };
      case 'Active':
         return {
            text: <Translate id="productsList:active" />,
            color: 'bg-light-green',
         };
      case 'Disabled':
         return {
            text: <Translate id="productsList:disabled" />,
            color: 'bg-grey-300',
         };
      case 'Rejected':
         return {
            text: <Translate id="productsList:rejected" />,
            color: 'bg-red',
         };
      case 'Requires Update':
         return {
            text: <Translate id="productsList:requires update" />,
            color: 'bg-blue',
         };
      case 'Draft':
         return {
            text: <Translate id="productsList:draft" />,
            color: 'bg-light-orange',
         };
   }
};

const ProductList = () => {
   const { lang } = useTranslate();
   const router = useRouter();
   const [searchTerm, setSearchTerm] = useState<string | null>(null);
   const [statusFilter, setStatusFilter] = useState<string | null>();
   const [pageNumber, setPageNumber] = useState(1);
   const [open, setOpen] = useState(false);
   const [selectedProduct, setSelectedProduct] = useState<ProductProps | undefined>(undefined);
   // const [productId, setProductId] = useState('');
   // const [productName, setProductName] = useState('');
   // const [openRefundTicket, setOpenRefundTicket] = useState(false);
   const { user, updateUser } = useAuth();
   const [showCreateActions, setShowCreateActions] = useState(false);
   const createActionsToggler = () => setShowCreateActions(!showCreateActions);
   const hideCreateActions = () => setShowCreateActions(false);
   const [openCreateProduct, setOpenCreateProduct] = useState(false);
   const { data: userProfileData } = useUserProfile({
      onSuccess: data => {
         if (data && data.status) {
            updateUser({ ...user, ...data });
         }
      },
   });

   const { data: bnplStatusData, refetch: refetchBnplStatus } = useBnplStatus();

   const {
      data: QueryData,
      isError: isListError,
      isLoading,
      refetch: refetchProducts,
      isFetching,
   } = useProductList({ searchTerm, statusFilter, pageNumber, lang });

   useEffect(() => {
      refetchBnplStatus();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [user]);

   if (isListError) return <div></div>;

   return (
      <>
         <Section className="py-8 mt-5">
            <div className="px-4 space-y-5 lg:px-8">
               <div className="flex flex-col justify-between gap-4 text-black md:flex-row md:columns-2 ">
                  <div>
                     <div className="mb-3 text-xl font-bold sm:text-3xl">
                        <Translate id="productsList:my products" />
                     </div>
                     <div className="text-sm whitespace-pre-line">
                        <Translate id="productsList:my products description" />
                     </div>
                  </div>
                  <div className="flex items-start gap-4">
                     <Dropdown
                        toggleMenu={hideCreateActions}
                        listClassName="!top-14 !rounded-md"
                        toggler={
                           <Button
                              onClick={() => createActionsToggler()}
                              className="w-full sm:w-60 h-12 flex gap-2 items-center focus:!outline-transparent focus:!ring-0"
                              disabled={!!(userProfileData?.status !== 'Active')}
                              theme="primary">
                              <div>
                                 <Translate id="productsList:create_product" />
                              </div>
                              <div>
                                 <Arrow
                                    className={cn(
                                       showCreateActions && 'rotate-180',
                                       'transition [&>*]:fill-white'
                                    )}
                                 />
                              </div>
                           </Button>
                        }
                        open={showCreateActions}>
                        <div className="w-52">
                           <div className="flex flex-col">
                              <Button
                                 onClick={() => {
                                    createActionsToggler();
                                    setOpenCreateProduct(true);
                                 }}
                                 className="w-full flex gap-2 items-center !text-sm !border-none !outline-transparent !ring-0">
                                 <PlusIcon width={10} height={10} />
                                 <span className="pl-0.5">
                                    <Translate id="productsList:create new product" />
                                 </span>
                              </Button>
                              <Button
                                 onClick={() => {
                                    createActionsToggler();
                                    router.push(
                                       `/${lang}/products/main-details?show_templates=true`
                                    );
                                 }}
                                 className="w-full flex gap-2 items-center !text-sm !border-none !outline-transparent !ring-0">
                                 <WizardIcon width={15} height={15} />
                                 <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#318139] to-[#28AFBE]">
                                    <Translate id="productsList:start from template" />
                                 </span>
                              </Button>
                           </div>
                        </div>
                     </Dropdown>
                     <Button
                        onClick={() => router.push(`/${lang}/promocodes`)}
                        className="w-full h-12 sm:w-60"
                        disabled={
                           !!(userProfileData?.status !== 'Active') || QueryData?.data?.length === 0
                        }
                        theme="outline-primary">
                        <Translate id="coupons:my coupons" />
                     </Button>
                     <Button
                        onClick={() => router.push(`/${lang}/discount-schedule`)}
                        className="w-full sm:w-60 h-12 flex gap-2 items-center focus:!outline-transparent focus:!ring-0"
                        theme="outline-primary"
                        disabled={
                           !!(userProfileData?.status !== 'Active') || QueryData?.data?.length === 0
                        }>
                        <Translate id="discountSchedule:price schedule" />
                     </Button>
                  </div>
               </div>

               {bnplStatusData?.bnplSupport && !bnplStatusData?.isBnplEnabled ? (
                  <BnplInfoCard
                     isBnplActive={bnplStatusData?.isBnplEnabled}
                     refetchBnplStatus={refetchBnplStatus}
                     tabbyServiceRate={bnplStatusData?.tabby_service_rate}
                     tamaraServiceRate={bnplStatusData?.tamara_service_rate}
                  />
               ) : null}
               <div className="h-full">
                  <div className="flex flex-col mb-5 space-y-2 xs:space-y-0 xs:flex-row xxs:justify-between">
                     <div className="grid w-full grid-cols-2 gap-6 lg:grid-cols-4 lg:grid-rows-1">
                        <div className="col-span-3 lg:col-span-3">
                           <SearchFilter onSearch={(text: string) => setSearchTerm(text)} />
                        </div>

                        <div className="col-span-3 lg:col-span-1">
                           <StatusFilter
                              onChange={(status: string) => setStatusFilter(status)}
                              statusFilter={statusFilter || ''}
                           />
                        </div>
                     </div>

                     {/* <div className="flex w-full xs:justify-end">
               {status === 'Active' ? (
                  <NextLink
                     href={`/products/main-details`}
                     className="flex items-center justify-center w-full p-2 px-8 text-lg font-bold text-center text-white rounded xs:w-auto bg-primary hover:opacity-80">
                     <Translate id="productsList:create_product" />
                  </NextLink>
               ) : (
                  <Button
                     theme="secondary"
                     className="flex items-center justify-center w-full p-2 px-8 text-lg font-bold text-center rounded xs:w-auto"
                     disabled={true}>
                     <Translate id="productsList:create_product" />
                  </Button>
               )}
            </div> */}
                  </div>

                  <div className="h-full overflow-auto">
                     {isLoading ? (
                        <div className="flex justify-center h-36">
                           <SubmitLoader />
                        </div>
                     ) : (
                        <div className="grid grid-cols-4">
                           <div className="col-span-4">
                              <table className="w-full text-sm min-w-[550px]">
                                 <thead className="uppercase">
                                    <tr>
                                       <th className="px-4 text-left text-gray-400 bg-gray-100 rtl:text-right h-11 first:rounded-md last:rounded-md">
                                          <Translate id="common:product" />
                                       </th>
                                       {/* <th className="text-left text-gray-400 bg-gray-100 h-11 first:rounded-md last:rounded-md">
                        <Translate id="common:city" />
                     </th> */}
                                       <th className="text-left text-gray-400 bg-gray-100 rtl:text-right h-11 first:rounded-md last:rounded-md">
                                          <Translate id="common:status" />
                                       </th>
                                       <th className="text-left text-gray-400 bg-gray-100 rtl:text-right h-11 first:rounded-md last:rounded-md">
                                          <Translate id="productsList:availability" />
                                       </th>
                                       {/* <th className="text-center text-gray-400 bg-gray-100 h-11 first:rounded-md last:rounded-md">
                                 <Translate id="common:booking status" />
                              </th> */}

                                       <th className="px-4 text-right text-gray-400 bg-gray-100 rtl:text-left h-11 first:rounded-md last:rounded-md">
                                          <Translate id="dashboard:action" />
                                       </th>
                                    </tr>
                                    {QueryData?.data.length === 0 && (
                                       <tr>
                                          <td colSpan={3} className="py-6 text-center">
                                             <span className="w-full py-8 text-xs text-center xl:text-sm text-grey-200">
                                                <Translate id="productsList:no products" />
                                             </span>
                                          </td>
                                       </tr>
                                    )}
                                 </thead>

                                 <tbody className="text-center">
                                    {QueryData?.data.map((product: ProductProps, index: number) => (
                                       <tr
                                          key={index}
                                          className="border-b border-gray-200 border-dashed hover:bg-gray-50">
                                          <td className="flex items-center w-fit">
                                             <div className="m-2">
                                                <NextLink href={`/products/${product._id}`}>
                                                   <Image
                                                      width={40}
                                                      height={32}
                                                      quality={30}
                                                      layout="fixed"
                                                      src={
                                                         product.default_cover_image ||
                                                         '/images/product.jpg'
                                                      }
                                                      className="object-cover w-16 h-12 rounded-lg "
                                                   />
                                                </NextLink>
                                             </div>
                                             <NextLink
                                                href={`/products/${product._id}`}
                                                className="flex items-end flex-shrink-0 gap-4 mx-4 text-lg leading-none whitespace-pre ">
                                                {product.name[lang] || product.name.en}
                                                <span className="flex items-center gap-2 text-xs text-grey-300">
                                                   <Translate id="productsList:preview" />

                                                   <EyeIcon className="fill-current text-grey-300" />
                                                </span>
                                             </NextLink>
                                          </td>
                                          {/* <td className="text-left">
                           <span>{product.primary_destination}</span>
                        </td> */}
                                          <td className="text-left ">
                                             <span
                                                className={cn(
                                                   getStatus(product.status)?.color,
                                                   'flex whitespace-nowrap rounded-full py-1 px-2 text-white text-center items-center justify-center text-sm capitalize min-w-[100px] w-fit'
                                                )}>
                                                {getStatus(product.status)?.text}
                                             </span>
                                          </td>
                                          <td className="text-left rtl:text-right ">
                                             {product?.duration && product?.booking_start_from && (
                                                <Duration
                                                   booking_date={product?.booking_start_from?.date}
                                                   duration={product?.duration}
                                                   duration_unit={product?.duration_unit}
                                                />
                                             )}
                                          </td>
                                          {/* <td className="text-center">
                                    <span
                                       className={cn(
                                          'h-5 rounded-xl px-2 text-[11px] whitespace-nowrap font-bold uppercase',
                                          {
                                             'bg-[#eaf7ef]  text-[#00983F]':
                                                product.booking_closed === false,
                                          },
                                          {
                                             'bg-[#fceef0]  text-[#DC3545]':
                                                product.booking_closed === true,
                                          }
                                       )}>
                                       {product.booking_closed ? (
                                          <Translate id={'common:closed'} />
                                       ) : (
                                          <Translate id={'common:available'} />
                                       )}
                                    </span>
                                 </td> */}

                                          <td>
                                             <div className="flex items-center justify-end flex-shrink-0 mx-4 space-x-8 rtl:space-x-reverse whitespace-nowrap">
                                                {product.status === 'Active' &&
                                                   !product.booking_closed && (
                                                      <div className="flex items-center flex-shrink-0">
                                                         <NextLink
                                                            href={`/products/${product._id}/create-payment-link`}
                                                            className="flex items-center justify-center flex-shrink-0 gap-2 text-sm font-semibold text-grey-100 h-fit">
                                                            <WalletIcon />
                                                            <Translate id="productsList:create_payment_link" />
                                                         </NextLink>
                                                      </div>
                                                   )}

                                                <div>
                                                   {product.status !== 'Under Review' ? (
                                                      <NextLink
                                                         href={`/products/${product._id}/edit/main-details`}
                                                         className="flex items-center justify-center gap-2 text-sm font-semibold text-grey-100 h-fit">
                                                         <EditIcon />
                                                         <Translate id="common:edit" />
                                                      </NextLink>
                                                   ) : (
                                                      <Button
                                                         loading={
                                                            !open &&
                                                            isFetching &&
                                                            selectedProduct !== undefined
                                                         }
                                                         onClick={() => {
                                                            setOpen(true);
                                                            setSelectedProduct(product);
                                                         }}
                                                         className="!font-medium !text-sm !border-none !text-red !px-0 outline-none">
                                                         {!open &&
                                                         isFetching &&
                                                         selectedProduct !== undefined ? null : (
                                                            <>
                                                               <CloseIcon className="fill-current text-red w-5" />
                                                               <Translate id="productDetails:withdraw" />{' '}
                                                            </>
                                                         )}
                                                      </Button>
                                                   )}
                                                </div>
                                                {/* <DeleteProductButton
                                                setOpenRefundTicket={setOpenRefundTicket}
                                                setOpen={setOpen}
                                                setProductId={setProductId}
                                                product={product}
                                                setProductName={setProductName}
                                             /> */}
                                             </div>
                                          </td>
                                       </tr>
                                    ))}
                                 </tbody>
                                 {/* <DeleteProductModal
                                    productName={productName}
                                    productId={productId}
                                    open={open}
                                    setOpen={setOpen}
                                 />
                                 <RefundTicketsModal
                                    productId={productId}
                                    description={
                                       <Translate
                                          parseHTML
                                          id="productDetails:refund tickets delete description"
                                          values={{ productName: productName }}
                                       />
                                    }
                                    open={openRefundTicket}
                                    setOpen={setOpenRefundTicket}
                                 /> */}
                                 <CreateProductModal
                                    open={openCreateProduct}
                                    setOpen={setOpenCreateProduct}
                                 />
                                 <UnpublishProductModal
                                    open={open}
                                    setOpen={setOpen}
                                    productName={
                                       selectedProduct?.name?.[lang] ||
                                       selectedProduct?.name?.en ||
                                       ''
                                    }
                                    productId={selectedProduct?._id || ''}
                                    isUnderReview
                                    onClose={(hasStatusChanged?: boolean) => {
                                       if (!hasStatusChanged) {
                                          setSelectedProduct(undefined);
                                          return;
                                       }
                                       refetchProducts?.().then(() => {
                                          setSelectedProduct(undefined);
                                       });
                                    }}
                                 />
                              </table>
                           </div>
                        </div>
                     )}
                  </div>
               </div>
            </div>
         </Section>
         {QueryData?.meta && QueryData.meta.total > QueryData.meta.per_page && (
            <div className="flex justify-center my-5">
               <Pagination
                  back={() => setPageNumber(prev => prev - 1)}
                  next={() => setPageNumber(prev => prev + 1)}
                  currentPage={QueryData.meta.current_page}
                  lastPage={QueryData.meta.last_page}
                  numberOfElementsInOnePage={QueryData.meta.per_page}
                  totalElements={QueryData.meta.total}
               />
            </div>
         )}
      </>
   );
};

// type DeleteProductButtonType = {
//    product: ProductProps;
//    setOpen: (value: boolean) => void;
//    setProductName: (value: string) => void;
//    setProductId: (value: string) => void;
//    setOpenRefundTicket: (value: boolean) => void;
// };

// const DeleteProductButton = ({
//    product,
//    setOpen,
//    setProductName,
//    setProductId,
//    setOpenRefundTicket,
// }: DeleteProductButtonType) => {
//    const { lang } = useTranslate();
//    const [showPopover, setShow] = useState(false);
//    let timeout: any;

//    const openPopover = () => {
//       clearTimeout(timeout);
//       setShow(true);
//       timeout = setTimeout(() => setShow(false), 5000);
//    };
//    return (
//       <Popover
//          isOpen={showPopover}
//          positions={['top']}
//          padding={15}
//          containerClassName="z-30"
//          onClickOutside={() => setShow(false)}
//          content={({ position, childRect, popoverRect }) => (
//             <div>
//                <ArrowContainer // if you'd like an arrow, you can import the ArrowContainer!
//                   position={position}
//                   childRect={childRect}
//                   popoverRect={popoverRect}
//                   arrowColor="white"
//                   arrowSize={10}
//                   className="popover-arrow-container"
//                   arrowClassName="popover-arrow">
//                   <div
//                      className="px-6 py-5 bg-white rounded-default"
//                      style={{
//                         boxShadow: '0px 4px 72px rgba(0, 0, 0, 0.12)',
//                      }}>
//                      <Translate id="productDetails:can not delete product" />
//                   </div>
//                </ArrowContainer>
//             </div>
//          )}>
//          <Button
//             onClick={e => {
//                setProductId(product._id), setProductName(product.name[lang] || product.name.en);
//                if (product.status === 'Under Review') {
//                   e.preventDefault();
//                   openPopover();
//                } else {
//                   product.product_can_be_deleted ? setOpen(true) : setOpenRefundTicket(true);
//                }
//             }}
//             className="flex items-center justify-center !text-sm font-semibold p-0 ltr:pr-2 rtl:pl-2 ltr:!ml-4 rtl:!mr-4">
//             <div className="flex items-center gap-2 text-states-warning h-fit">
//                <TrashIconOutline />
//                <Translate id="common:delete" />
//             </div>
//          </Button>
//       </Popover>
//    );
// };

const Duration = ({
   booking_date,
   duration,
   duration_unit,
}: {
   booking_date: string;
   duration: string;
   duration_unit: string;
}) => {
   if (duration_unit === 'days') {
      return (
         <>
            {DateTime.fromISO(booking_date?.slice(0, 10)).toISODate() +
               ' - ' +
               DateTime.fromISO(booking_date?.slice(0, 10))
                  .plus({ days: Number(duration) })
                  .toISODate()}
         </>
      );
   }
   if (duration_unit === 'minutes') {
      return (
         <>
            {DateTime.fromISO(booking_date?.slice(0, 10)).toISODate() +
               ' - ' +
               DateTime.fromISO(booking_date?.slice(0, 10))
                  .plus({ minutes: Number(duration) })
                  .toISODate()}
         </>
      );
   } else {
      return <></>;
   }
};

export default ProductList;
